import { memo } from "react";
import clsx from "clsx";

import type {
  StrapiTariffCard,
  TariffCardVariant,
} from "@/shared/api/strapi";
import { Link } from "@/shared/ui/link/Link";
import { Marker } from "@/shared/ui/marker/Marker";

import styles from "./TariffCard.module.css";

interface TariffCardProps {
  data: StrapiTariffCard;
  index: number;
  billingCycle: "monthly" | "yearly";
}

const variantCardMap: Record<TariffCardVariant, string> = {
  DEFAULT: styles.default,
  ACCENT: styles.accent,
  DISABLED: styles.disabled,
};

const formatter = new Intl.NumberFormat("ru-RU");

export const TariffCard = memo(function TariffCard({
  data,
  index,
  billingCycle,
}: TariffCardProps) {
  const {
    title,
    description,
    priceCurrentMonth,
    priceBaseMonth,
    priceCurrentYear,
    priceBaseYear,
    pricePeriod,
    features,
    cta,
    isVisible,
    variant = "DEFAULT",
  } = data;

  if (!isVisible) return null;

  const priceCurrent =
    billingCycle === "monthly" ? priceCurrentMonth : priceCurrentYear;
  const priceBase = billingCycle === "monthly" ? priceBaseMonth : priceBaseYear;

  return (
    <article
      itemScope
      itemType="https://schema.org/Product"
      className={clsx(styles.card, variantCardMap[variant])}
    >
      <h3 className={styles.name} itemProp="name">
        {title}
        <div aria-hidden="true" className={styles.indicator}>
          {Array.from({ length: index }, (_, i) => (
              <span key={i} />
            ))}
        </div>
      </h3>
      <p className={styles.description} itemProp="description">
        {description}
      </p>

      <div
        itemProp="offers"
        itemScope
        itemType="https://schema.org/Offer"
        className={styles.offers}
      >
        <meta
          itemProp="price"
          content={String(priceCurrent)}
        />
        <meta itemProp="priceCurrency" content="RUB" />
        <link itemProp="availability" href="https://schema.org/InStock" />

        <div
          itemProp="priceSpecification"
          itemScope
          itemType="https://schema.org/UnitPriceSpecification"
          className={styles.priceSpecification}
        >
          <ins className={styles.priceCurrent}>
            {formatter.format(priceCurrent)} ₽
          </ins>
          {priceBase && (
            <del className={styles.priceBase}>
              {formatter.format(priceBase)} ₽
            </del>
          )}

          <time dateTime="P1M" className={styles.pricePeriod}>
            <span itemProp="unitText">{pricePeriod}</span>
          </time>
        </div>
      </div>

      <ul className={styles.featuresList}>
        {features.map((feature) => (
          <li
            itemProp="additionalProperty"
            itemScope
            itemType="https://schema.org/PropertyValue"
            className={styles.featureItem}
            key={feature.id}
          >
            <Marker variant={feature.markerColor} className={styles.marker} />
            <span itemProp="name">{feature.text}</span>
          </li>
        ))}
      </ul>

      {cta && (
          <div
              itemProp="potentialAction"
              itemScope
              itemType="https://schema.org/Action"
              className={styles.cta}
          >
            <Link
              href={cta.href}
              type={cta.type}
              variant={cta.variant}
              className={styles.button}
            >
              {cta.text}
            </Link>
            <meta itemProp="name" content={`${cta.ariaLabel}`} />
          </div>
      )}
    </article>
  );
});
